import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getFundDetails, createFundContribution, createFundExpense, destroyFundContribution, updateFundContribution, destroyFundExpense, updateFundExpense } from '../../../api/ClubFundAPI';
import { getUsers } from '../../../api/UsersAPI';
import { getHeadsOfAccounts } from '../../../api/HeadOfAccountsAPI';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppPaginator from '../../AppPaginator';
import { handleApiError } from '../../../api/risapi';
import { AuthContext } from '../../AuthContext';

// Import the new form components
import AddIncomeForm from './AddIncomeForm';
import RecordExpenseForm from './RecordExpenseForm';
import { Can } from '../../../hooks/usePermissions';

const ClubFundPage = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [fundDetails, setFundDetails] = useState({ balance: 0, transactions: { data: [] } });
    const [isLoading, setIsLoading] = useState(true);
    const [activeForm, setActiveForm] = useState(null);
    const [editingRecord, setEditingRecord] = useState(null);
    const [triggerFetch, setTriggerFetch] = useState(false);

    // Form-related data
    const [members, setMembers] = useState([]);
    const [sponsors, setSponsors] = useState([]);
    const [incomeHeads, setIncomeHeads] = useState([]);
    const [expenseHeads, setExpenseHeads] = useState([]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            new Promise(r => getFundDetails({}, r)),
            new Promise(r => getUsers({ paginate: false }, r)),
            new Promise(r => getHeadsOfAccounts({ account_type: 'income', paginate: false }, r)),
            new Promise(r => getHeadsOfAccounts({ account_type: 'expense', paginate: false }, r)),
        ]).then(([fundRes, usersRes, incomeHeadsRes, expenseHeadsRes]) => {
            if (fundRes) setFundDetails(fundRes);
            if (usersRes.users) setMembers(usersRes.users.data || usersRes.users);
            if (incomeHeadsRes.heads_of_accounts) setIncomeHeads(incomeHeadsRes.heads_of_accounts.data || incomeHeadsRes.heads_of_accounts);
            if (expenseHeadsRes.heads_of_accounts) setExpenseHeads(expenseHeadsRes.heads_of_accounts.data || expenseHeadsRes.heads_of_accounts);
            setIsLoading(false);
        });
    }, [triggerFetch]);

    const resetForms = () => {
        setEditingRecord(null);
        setActiveForm(null);
    };

    const handleEditClick = (transaction) => {
        const record = transaction.transactionable;
        setEditingRecord(record);
        if (transaction.type === 'credit' && record) {
            setActiveForm('income');
        } else if (transaction.type === 'debit' && record) {
            setActiveForm('expense');
        }
    };

    const handleDeleteClick = (transaction) => {
        const apiCall = transaction.type === 'credit' ? destroyFundContribution : destroyFundExpense;
        const record = transaction.transactionable;
        Swal.fire({
            title: 'Are you sure?', text: `This will create a reversal entry for this transaction.`, icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, reverse it!'
        }).then((result) => {
            if (result.isConfirmed) {
                apiCall(record, (response) => {
                    if (response.success) {
                        Swal.fire('Reversed!', 'The transaction has been reversed.', 'success');
                        setTriggerFetch(p => !p);
                    } else {
                        Swal.fire('Error!', response?.response?.data?.message || 'Could not reverse the transaction.', 'error');
                    }
                });
            }
        });
    };

    const handleSubmitSuccess = () => {
        resetForms();
        setTriggerFetch(p => !p);
    };

    if (isLoading) return <div className="text-center p-5"><div className="spinner-border"></div></div>;

    return (
        <main>
            <div className="container-fluid">
                <AppBreadCrumb title="Club Fund" />
                <div className="row">
                    <div className="col-12">
                        <div className="card text-center bg-primary text-white mb-4">
                            <div className="card-body">
                                <h5 className="card-title">Current Fund Balance</h5>
                                <h1 className="display-4">₹{Number(fundDetails.balance).toFixed(2)}</h1>
                                <div className="mt-3">
                                     <Can any={['club-fund-add-income']}>
                                    <button className="btn btn-success me-2" onClick={() => { resetForms(); setActiveForm('income'); }}>
                                        <i className="fas fa-plus me-2"></i>Add Income
                                    </button>
                                    </Can>
                                    <Can any={['club-fund-add-expense']}>
                                    <button className="btn btn-danger" onClick={() => { resetForms(); setActiveForm('expense'); }}>
                                        <i className="fas fa-minus me-2"></i>Record Expense
                                    </button>
                                    </Can>
                                </div>
                            </div>
                        </div>
                    </div>

                    {activeForm === 'income' && (
                        <AddIncomeForm
                            editingRecord={editingRecord}
                            members={members}
                            sponsors={sponsors}
                            incomeHeads={incomeHeads}
                            onSuccess={handleSubmitSuccess}
                            onCancel={resetForms}
                        />
                    )}
                    {activeForm === 'expense' && (
                         <RecordExpenseForm
                            editingRecord={editingRecord}
                            expenseHeads={expenseHeads}
                            onSuccess={handleSubmitSuccess}
                            onCancel={resetForms}
                         />
                    )}

                    <div className="col-12">
                        <div className="card">
                           <div className="card-header"><h5>Transaction History</h5></div>
                           <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount</th><th>Balance</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fundDetails.transactions.data.map(tx => (
                                                <tr key={tx.id}>
                                                    <td>{new Date(tx.transaction_date).toLocaleString()}</td>
                                                    <td>{tx.description}</td>
                                                    <td><span className={`badge bg-light-${tx.type === 'credit' ? 'success' : 'danger'}`}> {tx.transactionable_type !='App\\Models\\Club'?tx.type:'opening balance'}</span></td>
                                                    <td>₹{tx.amount}</td>
                                                    <td>₹{Number(tx.balance_after_transaction).toFixed(2)}</td>
                                                    <td>
                                                        {tx.transactionable_type !='App\\Models\\Club'?
                                                        (
                                                        <div className="dropdown">
                                                            <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">Actions</button>
                                                            <ul className="dropdown-menu">
                                                                <li><a className="dropdown-item" href="#" onClick={() => handleEditClick(tx)}>Edit</a></li>
                                                                <li><a className="dropdown-item" href="#" onClick={() => handleDeleteClick(tx)}>Reverse (Delete)</a></li>
                                                            </ul>
                                                        </div>
                                                        ):''}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <AppPaginator />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default ClubFundPage;

